import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ssapi from 'lib/api/secondstepApi'
import { addNotification } from 'store/notifications'
import { NetSuiteAsset } from 'types'
import { getReadableErrorMessage } from 'utils/errorUtils'

const isDisplayableNetSuiteAsset = (asset: NetSuiteAsset): boolean => {
  if (asset.isActive) return true
  if (!asset.expirationDate) return true

  const expirationDate = new Date(asset.expirationDate)
  const sixMonthsAgo = new Date()
  sixMonthsAgo.setUTCMonth(sixMonthsAgo.getUTCMonth() - 6)

  return expirationDate >= sixMonthsAgo
}

type LoadedSiteNetSuiteAssets = {
  siteId: number
  assets: NetSuiteAsset[]
}

export const useSiteNetSuiteAssets = (
  siteId: number | undefined,
): NetSuiteAsset[] | undefined => {
  const dispatch = useDispatch()
  const [loadedAssets, setLoadedAssets] = useState<
    LoadedSiteNetSuiteAssets | undefined
  >()

  const siteNetSuiteAssets =
    siteId != null && !Number.isNaN(siteId) && loadedAssets?.siteId === siteId
      ? loadedAssets.assets
      : undefined

  useEffect(() => {
    if (!siteId || Number.isNaN(siteId)) {
      return
    }

    let cancelled = false

    const loadSiteNetSuiteAssets = async () => {
      try {
        const response = await ssapi.getNetSuiteAssets({ siteId })
        const assets: NetSuiteAsset[] = Array.isArray(response.data)
          ? response.data.filter(isDisplayableNetSuiteAsset)
          : []

        if (!cancelled) {
          setLoadedAssets({ siteId, assets })
        }
      } catch (error) {
        if (!cancelled) {
          setLoadedAssets({ siteId, assets: [] })
          dispatch(
            addNotification({
              color: 'danger',
              text: getReadableErrorMessage(
                error,
                'An unexpected error occurred',
              ),
            }),
          )
        }
      }
    }

    loadSiteNetSuiteAssets()

    return () => {
      cancelled = true
    }
  }, [dispatch, siteId])

  return siteNetSuiteAssets
}
